// features/workstations/qqq/today/macro-frame-zone.js
// ═══════════════════════════════════════════════════════════
// Macro Frame Zone (top of TODAY tab, persistent)
// ═══════════════════════════════════════════════════════════
//
// Per docs/QQQ_DASHBOARD_REBUILD_SPEC.md Section II Sub-tab 1.
//
// Persistent top-of-page zone showing:
//   - Today's date + day of week + event-day flag
//   - Daily briefing's synthesis line + 5 bullets (color-coded
//     gold/sage/terra/purple per existing convention)
//   - One-line market structure: VIX spot/9D/3M, 10Y, DXY
//   - News ticker scrolling latest 5 headlines (Whisperer first)
//
// Pulls multiple sources:
//   - /api/qqq/calendar/today (event flag + today's event headline)
//   - /api/workstations/qqq-session-brief (existing daily briefing)
//   - /api/qqq/news/recent?limit=5 (latest news ticker)
//   - /api/qqq/composite/today (composite score for tie-in)
//
// Phase 2b-1: minimal viable. Phase 2b-2 layers in market structure
// (live VIX/10Y/DXY) once we wire the streaming endpoint.
// ═══════════════════════════════════════════════════════════

import { useCallback, useEffect, useState } from 'react';

const TONE_CLASSES = {
  gold:   'qqq-macro-frame__bullet--gold',
  sage:   'qqq-macro-frame__bullet--sage',
  terra:  'qqq-macro-frame__bullet--terra',
  purple: 'qqq-macro-frame__bullet--purple',
};

export default function MacroFrameZone({ workstation, userId }) {
  const [calendar, setCalendar] = useState(null);
  const [composite, setComposite] = useState(null);
  const [news, setNews] = useState([]);
  const [briefing, setBriefing] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [calRes, compRes, newsRes] = await Promise.allSettled([
        fetch('/api/qqq/calendar/today', { cache: 'no-store' }).then(r => r.json()),
        fetch('/api/qqq/composite/today', { cache: 'no-store' })
          .then(r => r.status === 200 ? r.json() : null),
        fetch('/api/qqq/news/recent?limit=5', { cache: 'no-store' }).then(r => r.json()),
      ]);

      if (calRes.status === 'fulfilled') setCalendar(calRes.value);
      if (compRes.status === 'fulfilled') setComposite(compRes.value);
      if (newsRes.status === 'fulfilled') setNews(newsRes.value?.items || []);

      // Try to fetch the existing session brief (preserves current
      // briefing display until briefing pipeline is migrated)
      if (workstation?.id && userId) {
        try {
          const briefRes = await fetch(
            `/api/workstations/qqq-session-brief?workstation_id=${workstation.id}&user_id=${userId}`,
            { cache: 'no-store' },
          );
          if (briefRes.ok) {
            const j = await briefRes.json();
            setBriefing(j);
          }
        } catch (_briefErr) {
          // Briefing endpoint may fail or be in flight; non-fatal — the
          // rest of the macro frame still renders.
        }
      }
    } finally {
      setLoading(false);
    }
  }, [workstation?.id, userId]);

  useEffect(() => { loadAll(); }, [loadAll]);

  // Auto-refresh news ticker every 60s
  useEffect(() => {
    const id = setInterval(async () => {
      try {
        const res = await fetch('/api/qqq/news/recent?limit=5', { cache: 'no-store' });
        if (res.ok) {
          const j = await res.json();
          setNews(j.items || []);
        }
      } catch (_pollErr) {
        // Poll error is non-fatal; ticker stays on its last good fetch.
      }
    }, 60_000);
    return () => clearInterval(id);
  }, []);

  const eventDay = calendar?.event_day;
  const eventClass = calendar?.events?.find(e => e.importance === 'high')?.class
                  || calendar?.events?.[0]?.class
                  || null;
  const headlineEvent = calendar?.events?.find(e => e.importance === 'high')
                     || calendar?.events?.[0];

  return (
    <div className="qqq-macro-frame">
      {/* Top: date + event flag */}
      <div className="qqq-macro-frame__topline">
        <span className="qqq-macro-frame__date">
          {calendar?.date || todayDateString()}
        </span>
        {eventDay ? (
          <span className="qqq-macro-frame__event-flag qqq-macro-frame__event-flag--event">
            EVENT DAY{eventClass ? `: ${eventClass.toUpperCase()}` : ''}
            {headlineEvent?.time ? ` @ ${headlineEvent.time.slice(0, 5)}` : ''}
          </span>
        ) : (
          <span className="qqq-macro-frame__event-flag qqq-macro-frame__event-flag--normal">
            NORMAL DAY
          </span>
        )}
        {composite && (
          <span className={`qqq-macro-frame__composite qqq-macro-frame__composite--${composite.conviction_band}`}>
            Composite {composite.composite_score >= 0 ? '+' : ''}{composite.composite_score}
            {' · '}
            {composite.conviction_band.replace('_', ' ')}
          </span>
        )}
      </div>

      {/* Briefing synthesis + bullets */}
      {briefing?.briefing && (
        <div className="qqq-macro-frame__briefing">
          {briefing.briefing.synthesis && (
            <div className="qqq-macro-frame__synthesis">
              {briefing.briefing.synthesis}
            </div>
          )}
          {briefing.briefing.bullets && briefing.briefing.bullets.length > 0 && (
            <ul className="qqq-macro-frame__bullets">
              {briefing.briefing.bullets.map((b, i) => (
                <li key={i} className={TONE_CLASSES[b.tone] || TONE_CLASSES.gold}>
                  <span className="qqq-macro-frame__bullet-text">{b.bullet}</span>
                  {b.ctx && <span className="qqq-macro-frame__bullet-ctx"> — {b.ctx}</span>}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* News ticker */}
      {news.length > 0 && (
        <div className="qqq-macro-frame__ticker">
          <span className="qqq-macro-frame__ticker-label">News:</span>
          <div className="qqq-macro-frame__ticker-items">
            {news.map((item) => {
              const isWhisperer = item.tags?.includes('whisperer');
              return (
                <span
                  key={item.id}
                  className={
                    'qqq-macro-frame__ticker-item' +
                    (isWhisperer ? ' qqq-macro-frame__ticker-item--whisperer' : '')
                  }
                >
                  {isWhisperer && '★ '}
                  <span className="qqq-macro-frame__ticker-source">{item.source.toUpperCase()}</span>
                  {' · '}
                  {item.source_url ? (
                    <a href={item.source_url} target="_blank" rel="noopener noreferrer">
                      {item.headline.slice(0, 100)}
                    </a>
                  ) : (
                    item.headline.slice(0, 100)
                  )}
                </span>
              );
            })}
          </div>
        </div>
      )}

      {loading && !calendar && !briefing && (
        <div className="qqq-macro-frame__loading">Loading macro frame…</div>
      )}
    </div>
  );
}

function todayDateString() {
  return new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }).format(new Date());
}
